import GNEAsyncPagination from 'components/shared/dropdown-with-pagination/GNEAsyncPagination';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import { ALL, PayerMixField } from 'constants/constants';
import { useRef, useState } from 'react';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { wrapMenuList } from 'react-select-async-paginate';
import { loadDropdownOption } from 'store/customer/customer-view-actions';
import {
  dropdownOptions,
  formatOptionLabel,
  isValidSearchText,
  ValueContainer,
} from 'utils/utils';

const PayerMixFilter = ({
  parentPayer,
  payer,
  bookOfBusiness,
  benifitType,
  product,
  indication,
  competitorBrand,
  ecosystem,
  handleFilterChange,
}) => {
  const firstLoading = useRef(true);
  const shouldLoadMore = useRef(false);

  const [offset, setOffset] = useState(0);

  const { roleId } = useSelector((state) => state?.Auth?.userClaimObject);
  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );

  const shouldLoadMoreOptions = (scrollHeight, clientHeight, scrollTop) => {
    if (firstLoading.current) {
      firstLoading.current = false;
      return true;
    }
    const bottomBorder = (scrollHeight - clientHeight) / 2;
    const loadMore = bottomBorder < scrollTop;
    shouldLoadMore.current = loadMore;
    return loadMore;
  };

  const loadOptions = async (inputValue, fieldName, dependency) => {
    if (!isValidSearchText(inputValue)) {
      return {
        options: [],
      };
    }

    // Build Expresssion
    const expression = buildDropdownExpression(dependency);

    const params = {
      offset: firstLoading.current ? 0 : !shouldLoadMore.current ? 0 : offset,
      mdmId: selectedCustomer?.value,
      entityType: selectedCustomer.flag,
      roleId,
      fieldName,
      searchText: inputValue === '' ? undefined : inputValue,
      expression: expression === '' ? undefined : expression,
    };

    const { values, currentListSize, hasMoreElement } =
      await loadDropdownOption(params);

    if (hasMoreElement) {
      setOffset((prevValue) => prevValue + currentListSize);
    }

    return {
      options: values,
      hasMore: hasMoreElement,
    };
  };

  const buildDropdownExpression = (dependency) => {
    let output = [];

    for (let key in dependency) {
      const fieldValue = dependency[key];
      if (fieldValue) {
        if (Array.isArray(fieldValue)) {
          // If it's an array, filter out empty values and format the output accordingly
          let values = fieldValue
            .filter((item) => item.value)
            .map((item) => item.value);

          if (values.length > 0) {
            const exp = '' + [key] + '={"' + values.join('","') + '"}';
            output.push(exp);
          }
        } else if (fieldValue.value) {
          output.push(`${key}={"${fieldValue.value}"}`);
        }
      }
    }
    return output.join(', ');
  };

  const handleOnChange = (value, field) => {
    handleFilterChange(value, field);
  };

  return (
    <>
      <Row>
        <Col md={2}>
          <label className='title__x-small'>Parent Payer</label>
          <GNEAsyncPagination
            value={parentPayer}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.ParentPayer);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.ParentPayer, {
                [PayerMixField.Payer]: payer,
                [PayerMixField.BookOfBusiness]: bookOfBusiness,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[selectedCustomer, payer, bookOfBusiness]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>Payer</label>
          <GNEAsyncPagination
            value={payer}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.Payer);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.Payer, {
                [PayerMixField.ParentPayer]: parentPayer,
                [PayerMixField.BookOfBusiness]: bookOfBusiness,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, parentPayer, bookOfBusiness]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>Book of Business</label>
          <GNEAsyncPagination
            value={bookOfBusiness}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.BookOfBusiness);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.BookOfBusiness, {
                [PayerMixField.ParentPayer]: parentPayer,
                [PayerMixField.Payer]: payer,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, parentPayer, payer]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>Benefit Type</label>
          <GNEAsyncPagination
            value={benifitType}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.BenefitType);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.BenefitType, {
                [PayerMixField.Product]: product,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, product]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>Product</label>
          <GNEAsyncPagination
            value={product}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.Product);
            }}
            placeholder={ALL}
            isMulti={false}
            closeMenuOnSelect={true}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.Product, {
                [PayerMixField.BenefitType]: benifitType,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, benifitType]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>
            Indication
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Select a single Indication to view Policy related data
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={indication}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.Indication);
            }}
            placeholder={'Select Indication'}
            isMulti={false}
            closeMenuOnSelect={true}
            isClearable
            isDisabled={!product?.value}
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.Indication, {
                [PayerMixField.Product]: product,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, product]}
          />
        </Col>
      </Row>
      <Row className='mt-2'>
        <Col md={2}>
          <label className='title__x-small'>
            Competitor Brand
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Select a single Competitor Brand to view Relative Access
                  Position
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={competitorBrand}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.CompetitorProduct);
            }}
            placeholder={'Select Competitor Brand'}
            isMulti={false}
            closeMenuOnSelect={true}
            isClearable
            isDisabled={!product?.value || !indication?.value}
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PayerMixField.CompetitorProduct, {
                [PayerMixField.Product]: product,
                [PayerMixField.Indication]: indication,
              });
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer, product, indication]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>
            Ecosystem
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Selecting an Ecosystem will limit the Payers viewed to those
                  operating in the Ecosystem. This filter will affect Sales
                  metrics.
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={ecosystem}
            onChange={(value) => {
              handleOnChange(value, PayerMixField.EcosystemName);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                PayerMixField.EcosystemName,
                null
              );
            }}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            cacheUniqs={[selectedCustomer]}
          />
        </Col>
      </Row>
    </>
  );
};

export default PayerMixFilter;
